/**
 * Iter 245 — Suggestions de réapprovisionnement (semi-auto).
 *
 * Affiche les suggestions générées par le système (stock < seuil)
 * et permet de les convertir en PO en 1 clic.
 */
import { useEffect, useState, useMemo } from "react";
import { useAuth } from "../../contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import PageHeader from "../../components/PageHeader";
import { Button } from "../../components/ui/button";
import { Bell, Loader2, CheckCircle2, X, AlertTriangle, ArrowRight } from "lucide-react";
import { toast } from "sonner";

export default function RestockSuggestionsPage() {
  const { axiosAuth } = useAuth();
  const api = useMemo(() => axiosAuth(), [axiosAuth]);
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/inventory/restock-suggestions");
      setItems(r.data.items || []);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const approve = async (id) => {
    setProcessing(id);
    try {
      const r = await api.post(`/inventory/restock-suggestions/${id}/approve`);
      toast.success("Commande fournisseur créée");
      await load();
      // Optional jump to PO detail
      if (r.data?.po?.po_id) {
        if (window.confirm("Voir la commande créée ?")) {
          navigate("/admin/inventory/purchase-orders");
        }
      }
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); } finally { setProcessing(null); }
  };

  const dismiss = async (id) => {
    if (!window.confirm("Ignorer cette suggestion ?")) return;
    try {
      await api.post(`/inventory/restock-suggestions/${id}/dismiss`);
      toast.success("Suggestion ignorée");
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  if (loading) return <div className="p-8 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-[#f7c935]" /></div>;

  return (
    <div className="p-6 max-w-[1400px] mx-auto space-y-5" data-testid="restock-page">
      <PageHeader
        icon={<Bell />}
        title="Suggestions de réapprovisionnement"
        description="Stocks en dessous du seuil — proposez 1-clic une commande au fournisseur par défaut."
      />

      {items.length === 0 ? (
        <div className="bg-[#14101c] border border-white/5 rounded-xl p-12 text-center text-slate-500" data-testid="empty-state">
          <CheckCircle2 className="w-10 h-10 mx-auto text-emerald-400 mb-3" />
          <p className="text-white font-bold mb-1">Tout est sous contrôle</p>
          <p className="text-sm">Aucun produit n'est sous le seuil de réappro.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {items.map((sug) => {
            const total = (sug.suggested_qty * sug.unit_cost_eur).toFixed(2);
            const canApprove = !!sug.supplier_id;
            return (
              <div key={sug.suggestion_id} className="bg-[#14101c] border border-amber-500/30 rounded-xl p-4" data-testid={`suggestion-${sug.suggestion_id}`}>
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4 text-amber-400" />
                    <span className="text-[10px] uppercase font-bold text-amber-400">Stock bas</span>
                  </div>
                  <Button size="icon" variant="ghost" onClick={() => dismiss(sug.suggestion_id)} className="hover:text-slate-300" data-testid={`dismiss-${sug.suggestion_id}`}>
                    <X className="w-3.5 h-3.5" />
                  </Button>
                </div>
                <h3 className="text-white font-bold">{sug.product_name}</h3>
                <p className="text-[11px] font-mono text-slate-500 mb-3">{sug.product_sku}</p>
                <div className="grid grid-cols-2 gap-2 text-xs mb-3">
                  <Stat label="Stock actuel" value={sug.current_qty} color="#ef4444" />
                  <Stat label="Seuil" value={sug.threshold} color="#94a3b8" />
                </div>
                <div className="bg-white/[0.02] rounded p-2 mb-3 text-xs">
                  <div className="flex justify-between">
                    <span className="text-slate-500">À commander :</span>
                    <span className="text-white font-bold">{sug.suggested_qty} unités</span>
                  </div>
                  <div className="flex justify-between mt-1">
                    <span className="text-slate-500">PU achat :</span>
                    <span className="text-slate-300">{sug.unit_cost_eur?.toFixed(2)} €</span>
                  </div>
                  <div className="flex justify-between mt-1 pt-1 border-t border-white/5">
                    <span className="text-slate-400">Total HT :</span>
                    <span className="text-white font-bold">{total} €</span>
                  </div>
                </div>
                <div className="text-[11px] text-slate-400 mb-3">
                  <span className="text-slate-500">Fournisseur : </span>
                  {sug.supplier_name}
                </div>
                <Button
                  size="sm"
                  onClick={() => approve(sug.suggestion_id)}
                  disabled={!canApprove || processing === sug.suggestion_id}
                  className="w-full bg-[#f7c935] hover:bg-[#e6b825] text-[#14101c]"
                  data-testid={`approve-${sug.suggestion_id}`}
                >
                  {processing === sug.suggestion_id ? <Loader2 className="w-4 h-4 animate-spin" /> : <>
                    Créer la commande <ArrowRight className="w-3.5 h-3.5 ml-1.5" />
                  </>}
                </Button>
                {!canApprove && <p className="text-[10px] text-red-400 mt-1.5 text-center">⚠️ Définissez un fournisseur par défaut sur ce produit</p>}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

const Stat = ({ label, value, color }) => (
  <div className="bg-white/[0.02] rounded px-2 py-1.5">
    <div className="text-[10px] text-slate-500 uppercase">{label}</div>
    <div className="font-bold" style={{ color }}>{value}</div>
  </div>
);
